import React, { useCallback, useEffect, useState } from 'react';
import { api, btnPrimary, btnSecondary, card, input, labelCls, Modal, Skeleton, useToast } from '../ui/Kit';
import { Evidence, PrivacyHint } from './common';

// Bundle 4b — the match grade (CandSearch L / CandProfile M), the per-job match panel with recruiter overrides of a
// must-have (audited), the authenticity checklist (separate from the match, never rejects) and the weekly weight
// suggestions stub (the hiring manager approves / declines; the weights never change on their own).

const GRADE_TONE = { A: 'bg-[#DCFCE7] text-[#166534]', B: 'bg-[#E0F2FE] text-[#075985]', C: 'bg-[#FEF3C7] text-[#92400E]', D: 'bg-[#F1F5F9] text-[#475569]' };
const RISK = {
  low: { cls: 'bg-[#DCFCE7] text-[#166534]', icon: '●', label: 'Low risk' },
  review: { cls: 'bg-[#FEF3C7] text-[#92400E]', icon: '◆', label: 'Review' },
  high: { cls: 'bg-[#FEE2E2] text-[#991B1B]', icon: '▲', label: 'High risk' },
};
const IC = { pass: ['✓', 'bg-[#DCFCE7] text-[#166534]'], fail: ['✗', 'bg-[#FEE2E2] text-[#991B1B]'], unknown: ['?', 'bg-[#FEF3C7] text-[#92400E]'] };
const CHECK_IC = { clear: ['✓', 'bg-[#DCFCE7] text-[#166534]'], flag: ['▲', 'bg-[#FEE2E2] text-[#991B1B]'], not_checked: ['…', 'bg-[#F1F5F9] text-[#64748B]'] };

export function GradeTile({ grade, total, size = 'md' }) {
  const big = size === 'lg';
  return (
    <div className={`${big ? 'w-[72px] h-[72px]' : 'w-16 h-16'} rounded-[14px] flex flex-col items-center justify-center font-extrabold shrink-0 ${GRADE_TONE[grade] || GRADE_TONE.D}`}
      aria-label={`Grade ${grade}, ${Math.round(total)}%`} data-testid="grade">
      <b className="text-2xl leading-none">{grade}</b><small className="text-xs font-bold mt-0.5">{Math.round(total)}%</small>
    </div>
  );
}

export function RiskBadge({ risk }) {
  const r = RISK[risk];
  if (!r) return null;
  return <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full ${r.cls}`} data-testid="risk"><span aria-hidden="true">{r.icon}</span>Authenticity: {r.label}</span>;
}

const STATUS_LINE = {
  not_scored: 'Doesn’t meet the must-haves, so it isn’t AI-scored. After a call, override a must-have to include them.',
  failed: 'Couldn’t score this yet — it retries automatically.',
};

const Icon = ({ pair }) => <span aria-hidden="true" className={`w-5 h-5 rounded-md flex items-center justify-center text-xs font-extrabold ${pair[1]}`}>{pair[0]}</span>;

function OverrideDialog({ m, onClose, onSave }) {
  const [result, setResult] = useState(m.result === 'pass' ? 'fail' : 'pass');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const save = async () => {
    setBusy(true); setError(null);
    try { await onSave(m.key, result, reason.trim()); } catch (e) { setError(e.message); setBusy(false); }
  };
  return (
    <Modal open title={`Override · ${m.label}`} onClose={onClose}
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} disabled={busy || reason.trim().length < 3} onClick={save} data-testid="override-save">Save override</button></>}>
      <p className="text-sm text-[#475569]">Scored as <b>{m.scored_result || m.result}</b>{m.detail ? ` — ${m.detail}` : ''}. Your change and reason are recorded in the activity and audit log.</p>
      <label className={labelCls + ' mt-3'} htmlFor="ov-result">Result</label>
      <select id="ov-result" className={input} value={result} onChange={(e) => setResult(e.target.value)}>
        <option value="pass">Meets it</option><option value="fail">Doesn’t meet it</option><option value="unknown">Unknown (verify)</option>
        {m.overridden && <option value="clear">Remove my override</option>}
      </select>
      <label className={labelCls + ' mt-3'} htmlFor="ov-reason">Reason</label>
      <textarea id="ov-reason" className={input} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Confirmed on the call: 9 years of Selenium at Infosys" />
      <PrivacyHint />
      {error && <p className="text-sm text-[#B91C1C] mt-2">{error}</p>}
    </Modal>
  );
}

export function MatchPanel({ candidateId, jobId, jobs, onJob }) {
  const toast = useToast();
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => {
    setError(null);
    return api(`/api/candidates/${candidateId}/match/${jobId}`).then(setD).catch((e) => setError(e));
  }, [candidateId, jobId]);
  useEffect(() => { setD(null); load(); }, [load]);
  useEffect(() => {
    if (!d || ['done', 'failed', 'not_scored'].includes(d.status)) return undefined;
    const t = setTimeout(load, 2500);
    return () => clearTimeout(t);
  }, [d, load]);
  const save = async (key, result, reason) => {
    const r = await api(`/api/candidates/${candidateId}/match/${jobId}/override`, { method: 'POST', body: { key, result, reason } });
    setD(r); setEdit(null); toast.show('Override saved');
  };
  const picker = jobs.length > 1 && (
    <select className={input + ' w-auto'} aria-label="Job" value={jobId} onChange={(e) => onJob(Number(e.target.value))}>
      {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}</select>
  );
  if (error) return <section className={card + ' p-5 rounded-2xl'}><p className="text-sm text-[#B91C1C]">{error.message}</p></section>;
  if (!d) return <section className={card + ' p-5 rounded-2xl space-y-2'}><Skeleton className="h-5 w-1/3" /><Skeleton /><Skeleton /></section>;
  const scored = d.status === 'done';
  const musts = (d.must_haves || []).filter((m) => m.on);
  const met = musts.filter((m) => m.result === 'pass').length;
  const crits = scored ? d.criteria.filter((c) => c.on) : [];
  return (
    <section className={card + ' p-5 rounded-2xl'} data-testid="match-panel">
      <div className="flex items-center gap-3 flex-wrap">
        {scored && <GradeTile grade={d.grade} total={d.total} />}
        <div className="flex-1 min-w-0">
          <h2 className="text-[15px] font-extrabold">{scored ? `Why ${d.grade} · ${Math.round(d.total)}%${d.verify ? ' · verify' : ''}` : `Match · ${d.job.title}`}</h2>
          {scored ? <p className="text-xs text-[#64748B]">{d.job.title}{d.why ? ' — ' : ''}<Evidence text={d.why} quote={false} max={200} /></p>
            : <p className="text-sm text-[#64748B] mt-0.5" data-testid="match-status">{STATUS_LINE[d.status] || 'Scoring…'}</p>}
        </div>
        {picker}
      </div>
      {d.must_haves && <><div className="grid grid-cols-[22px_1fr] gap-x-2.5 gap-y-1 py-2.5 mt-3 border-t border-[#EEF1F6] text-[13px]">
        <Icon pair={IC[met === musts.length ? 'pass' : musts.some((m) => m.result === 'fail') ? 'fail' : 'unknown']} />
        <div><b>Must-haves: {met} of {musts.length} met</b></div>
      </div>
      <ul data-testid="must-haves">
        {d.must_haves.map((m) => (
          <li key={m.key} className={`grid grid-cols-[22px_1fr_auto] gap-x-2.5 py-2 border-t border-[#EEF1F6] text-[13px] ${m.on ? '' : 'opacity-50'}`}>
            <Icon pair={IC[m.result] || IC.unknown} />
            <div className="min-w-0"><span className="font-semibold" title={m.original && m.original !== m.label ? `Intake: ${m.original}` : undefined}>{m.label}</span>{m.detail && m.detail !== m.label ? <span className="text-[#475569]"> — {m.detail}</span> : null}
              <Evidence text={m.evidence} className="block text-[#475569] text-[12.5px] italic mt-0.5" /></div>
            <button type="button" className="text-xs font-semibold text-[#0066CC] self-start" onClick={() => setEdit(m)} data-testid={`override-${m.key}`}>{m.overridden ? 'Overridden · change' : 'Override'}</button>
          </li>
        ))}
      </ul></>}
      {scored && <ul className="mt-2" data-testid="criteria-scores">
        {crits.map((c) => {
          const frac = c.weight ? c.points / c.weight : 0;
          return (
            <li key={c.key} className="grid grid-cols-[22px_1fr] gap-x-2.5 py-2.5 border-t border-[#EEF1F6] text-[13px]">
              <Icon pair={frac >= 0.75 ? IC.pass : frac >= 0.4 ? ['~', 'bg-[#FEF3C7] text-[#92400E]'] : IC.fail} />
              <div><b>{c.label} — {Math.round(c.points)}/{c.weight}</b><Evidence text={c.evidence} className="block text-[#475569] text-[12.5px] italic mt-0.5" /></div>
            </li>
          );
        })}
      </ul>}
      <p className="text-xs text-[#64748B] mt-3">Scores never reject anyone — the recruiter decides. Name, photo, age signals, address and school prestige aren’t used.</p>
      {edit && <OverrideDialog m={edit} onClose={() => setEdit(null)} onSave={save} />}
    </section>
  );
}

export function AuthenticityPanel({ candidateId }) {
  const [d, setD] = useState(null);
  useEffect(() => { api(`/api/candidates/${candidateId}/authenticity`).then(setD).catch(() => setD(false)); }, [candidateId]);
  useEffect(() => {                                          // "See checklist →" from the search lands on #authenticity
    const el = d && window.location.hash === '#authenticity' && document.getElementById('authenticity');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'start' });
  }, [d]);
  if (d === false) return null;
  if (!d) return <section className={card + ' p-5 rounded-2xl'}><Skeleton className="h-5 w-1/2" /></section>;
  const r = RISK[d.risk] || RISK.low;
  return (
    <section id="authenticity" className={card + ' p-5 rounded-2xl'} data-testid="authenticity">
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <h2 className="text-[15px] font-extrabold flex-1">Authenticity checklist</h2>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${r.cls}`}>{r.label} · {d.clear} of {d.checked} clear</span>
      </div>
      <ul>
        {d.checks.map((c) => (
          <li key={c.key} className="flex gap-2.5 items-start py-[7px] border-t border-[#EEF1F6] text-[13px]">
            <Icon pair={CHECK_IC[c.status] || CHECK_IC.not_checked} />
            <span className="flex-1 min-w-0"><span className={c.status === 'not_checked' ? 'text-[#64748B]' : ''}>{c.label}</span>
              {c.detail && <span className="text-[#475569]"> — {c.detail}</span>}
              {c.status === 'not_checked' && c.connector && <span className="block text-xs text-[#94A3B8]">Not checked · {c.connector}</span>}</span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-[#64748B] mt-2">Signals to verify, never a reason to reject on their own.</p>
    </section>
  );
}

export function WeightSuggestions({ jobId }) {
  const toast = useToast();
  const [d, setD] = useState(null);
  const load = useCallback(() => api(`/api/candidates/jobs/${jobId}/weight-suggestions`).then(setD).catch(() => setD(false)), [jobId]);
  useEffect(() => { load(); }, [load]);
  if (!d || !(d.suggestions || []).length) return null;           // nothing generates them yet — the card appears once one exists
  const decide = async (s, approve) => {
    try { await api(`/api/candidates/weight-suggestions/${s.id}/decide`, { method: 'POST', body: { approve } }); toast.show(approve ? 'Approved' : 'Declined'); load(); }
    catch (e) { toast.show(e.message, 'error'); }
  };
  return (
    <section className={card + ' p-5 rounded-2xl'} data-testid="weight-suggestions">
      <h2 className="text-[15px] font-extrabold">Weekly weight suggestions</h2>
      <p className="text-xs text-[#64748B] mt-0.5">{d.note}</p>
      <ul className="mt-2">{d.suggestions.map((s) => (
          <li key={s.id} className="py-2.5 border-t border-[#EEF1F6] text-[13px] flex flex-wrap gap-2 items-center">
            <span className="flex-1 min-w-0"><b>Week of {s.week_start}</b>{s.rationale ? ` — ${s.rationale}` : ''}
              <span className="block text-xs text-[#64748B]">{Object.keys(s.suggested || {}).map((k) => `${k.replace(/_/g, ' ')} ${(s.current || {})[k] ?? '–'} → ${s.suggested[k]}`).join(' · ')}</span></span>
            {s.status === 'pending' && d.can_decide
              ? <><button className={btnPrimary} onClick={() => decide(s, true)}>Approve</button><button className={btnSecondary} onClick={() => decide(s, false)}>Decline</button></>
              : <span className="text-xs font-semibold text-[#475569] capitalize">{s.status}</span>}
          </li>))}</ul>
    </section>
  );
}
